import React from "react";
import CountUp from "react-countup";
import SVGRenderer from "../SVGRenderer";
import LazyImage from "components/elements/LazyImage/LazyImage";
import SmartLink from "components/SmartLink";
import Button from "components/elements/Button/Button";
import { isColorDark } from "helpers/contrastCalculator";

import styles from "./Card.module.scss";

/**
 * Card with secondary color background and appropriate contrast text
 * 
 * @param {Object} props - Component properties
 */
const CardSecondary = React.forwardRef(({ 
	inView,
	title, 
	svg, 
	lead, 
	desc, 
	cta_text, 
	cta_link, 
	image, 
	image_alt, 
	bg_image, 
	bg_image_alt, 
	bg_color = 'var(--bs-secondary)',
	className = "", 
	theme, 
	count_up
}, ref) => {
	// Extract variable name if it's a CSS variable
	const bgColorVar = bg_color.includes('var(') 
		? bg_color.match(/var\((.*?)[,)]/)[1].trim()
		: '--bs-secondary';
	
	// Check if background is dark
	const isDarkBg = isColorDark(bgColorVar);
	
	// Choose theme based on background brightness
	const cardTheme = isDarkBg ? "dark" : "light";
	
	// Choose button variant based on background brightness
	const buttonVariant = isDarkBg ? "light" : "primary";
	
	// Define text color CSS variable
	const textColor = `var(${bgColorVar}-contrast)`;
	
	return (
		<div
			ref={ref}
			data-bs-theme={cardTheme}
			style={{ backgroundColor: bg_color }}
			className={`${styles.CardSecondary} card h-100 rounded-4 shadow border-0 position-relative overflow-hidden ${className}`}>
			{image && (
				<div>
					<LazyImage
						src={image}
						alt={image_alt || title}
						className="card-img-top"
						fill_mode="cover"
						disableAbsolute={true}
					/>
				</div>
			)}
			<div className="card-body p-4 d-flex flex-column">
				{svg && (
					<div className="mb-3 text-center">
						<SVGRenderer svgString={svg} />
					</div>
				)}
				{inView && count_up && (
					<CountUp
						className="count-up display-5 fw-bold mb-3"
						style={{ color: isDarkBg ? textColor : 'var(--bs-primary)' }}
						end={count_up}
						duration={5}
						separator="."
					/>
				)}
				{title && (
					<h3 
						className="card-title title fs-4 mb-0"
						style={{ color: textColor }}>
						{title}
					</h3>
				)}
				{lead && (
					<p 
						className="lead mt-2" 
						style={{
							color: textColor,
							opacity: isDarkBg ? 0.85 : 0.75
						}}>
						{lead}
					</p>
				)}

				{desc && (
					<p
						className="card-text desc"
						style={{ color: textColor }}
						dangerouslySetInnerHTML={{ __html: desc }}></p>
				)}
				<div className="mt-auto">
					{cta_text && cta_link && (
						<SmartLink to={cta_link}>
							<Button
								size={"md"}
								variant={buttonVariant}
								letterCase="uppercase">
								{cta_text}
							</Button>
						</SmartLink>
					)}
				</div>
			</div>
			{bg_image && (
				<img
					src={bg_image}
					alt={bg_image_alt || ""}
					className="position-absolute w-100 h-100"
					style={{
						objectFit: "cover",
						opacity: 0.15,
						mixBlendMode: isDarkBg ? "soft-light" : "multiply",
						zIndex: 0,
						top: 0,
						left: 0,
					}}
					aria-hidden="true"
				/>
			)}
		</div>
	);
});

export default CardSecondary;
